// src/App.tsx
import React from 'react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProviderDashboard from './components/ProviderDashboard';
import PayerDashboard from './components/PayerDashboard';
import PostOfficeDashboard from './components/PostOfficeDashboard';
import Chatbot from './components/Chatbot';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { setNavigate } from './utils/navigation';

/**
 * Helper component to capture the navigate function from React Router
 * and share it with our non-React utilities (like axiosInstance).
 */
const NavigationSetter: React.FC = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) return <Login />;

  const renderDashboard = () => {
    // ✅ الأدمن يتحدد بالـ role مش company_type
    if (user?.role === 'admin') return <Dashboard />;

    switch (user?.company_type) {
      case 'provider':    return <ProviderDashboard />;
      case 'payer':       return <PayerDashboard />;
      case 'post-office': return <PostOfficeDashboard />;
      default:            return <Login />;
    }
  };

  const currentView = renderDashboard();
  const isLoginView = currentView.type === Login;

  return (
    <>
      {currentView}
      {isAuthenticated && !isLoginView && <Chatbot />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UploadProvider>
            <LoadingProvider>
              <NavigationSetter />
              <AppContent />
              {process.env.NODE_ENV === 'development' && (
                <ReactQueryDevtools initialIsOpen={false} />
              )}
            </LoadingProvider>
          </UploadProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;